import { CLASS_NAMES, SELECTORS, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the MegaBanner module
 */
export default class MegaBanner extends MessageBus {
  /**
   * Constructor function of the MegaBanner class. Must receive one arguments, a DOM
   * node representing a delivery ticket.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;
    this.KEY = 'care-mega-banner-closed';

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} MegaBanner A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} MegaBanner A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.mega_banner = this.element;
    this.mega_banner_expand_icon = this.mega_banner.querySelector(
      `.${CLASS_NAMES.MEGA_BANNER_EXPAND}`
    );
    this.mega_banner_toggle = this.mega_banner.querySelector(
      `.${CLASS_NAMES.TOGGLE_MEGA_BANNER}`
    );
    this.mega_banner_closed = this.mega_banner.querySelector(
      `.${CLASS_NAMES.MEGA_BANNER_CLOSED}`
    );
    this.mega_banner_buttons = this.mega_banner.querySelectorAll(
      SELECTORS.DONATE_VALUE
    );
    this.donation_level = this.mega_banner.querySelector(
      SELECTORS.DONATE_LEVEL
    );
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Search A reference to the current instance of the class
   * @chainable
   */
  enable() {
    const isHidden = sessionStorage.getItem(this.KEY);

    if (isHidden) {
      this.hideMegaBanner();
      this.handleResize();
    } else {
      this.showMegaBanner();
      this.handleResize();
    }

    if (this.mega_banner_closed) {
      const openBanner = () => {
        if (!this.mega_banner.classList.contains(CLASS_NAMES.IS_VISIBLE)) {
          this.showMegaBanner();
          this.handleResize();
        }
      };

      this.mega_banner_closed.addEventListener(EVENTS.CLICK, openBanner);
      this.mega_banner_closed.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openBanner();
        }
      });
    }

    if (this.mega_banner_expand_icon) {
      const toggleBanner = (e) => {
        e.stopPropagation();
        if (this.mega_banner.classList.contains(CLASS_NAMES.IS_VISIBLE)) {
          this.handleCloseClick();
        } else {
          this.showMegaBanner();
          this.handleResize();
        }
      };

      this.mega_banner_expand_icon.addEventListener(EVENTS.CLICK, toggleBanner);
      this.mega_banner_expand_icon.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleBanner(e);
        }
      });
    }

    if (this.mega_banner_buttons) {
      this.mega_banner_buttons.forEach((button) => {
        button.addEventListener(EVENTS.CLICK, () => {
          const getLevel = button.dataset.level;
          this.buttonSelect(button);
          this.donation_level.value = getLevel;
        });
      });
    }

    window.addEventListener(EVENTS.RESIZE, this.handleResize.bind(this));
    window.addEventListener(EVENTS.SCROLL, this.handleResize.bind(this));
    window.addEventListener(EVENTS.LOAD, this.handleResize.bind(this));
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleCloseClick = this.handleCloseClick.bind(this);
    return this;
  }

  showMegaBanner() {
    if (this.mega_banner) {
      this.mega_banner.classList.add(CLASS_NAMES.IS_VISIBLE);
      this.mega_banner_closed.classList.add(CLASS_NAMES.HIDDEN_ITEMS);
      this.mega_banner.classList.add(CLASS_NAMES.OPEN_MEGA_BANNER);

      this.mega_banner_closed.setAttribute('aria-expanded', 'true');
    }
  }

  toggleMegaBanner() {
    if (this.mega_banner) {
      this.mega_banner.classList.toggle(CLASS_NAMES.IS_VISIBLE);
      this.mega_banner_closed.classList.toggle(CLASS_NAMES.HIDDEN_ITEMS);
    }
  }

  hideMegaBanner() {
    if (this.mega_banner) {
      this.mega_banner.classList.remove(CLASS_NAMES.IS_VISIBLE);
      this.mega_banner_closed.classList.remove(CLASS_NAMES.HIDDEN_ITEMS);
      this.mega_banner.classList.add(CLASS_NAMES.TOGGLE_MEGA_BANNER);

      this.mega_banner_closed.setAttribute('aria-expanded', 'false');
    }
  }

  handleCloseClick() {
    this.handleResize();
    this.hideMegaBanner();
    sessionStorage.setItem(this.KEY, true);
  }

  buttonSelect(button) {
    if (this.mega_banner_buttons) {
      this.mega_banner_buttons.forEach((item) => {
        item.removeAttribute('checked');
        item.parentElement.classList.remove('checked');
      });
      button.setAttribute('checked', true);
      button.parentElement.classList.add('checked');
    }
  }

  handleResize() {
    if (this.mega_banner) {
      const bodyHeight =
        document.offsetHeight || document.documentElement.offsetHeight || 0;
      document.body.style.setProperty('--height-body', `${bodyHeight}px`);
      document.body.classList.add('has-height-body');
    }
  }
}
